import React, { useEffect, useState, useRef } from "react";

const TOUR_VERSION = "1.0.0"; // bump this to force showing the tour again after updates

const defaultSteps = [
  {
    target: ".dsv2-ref",
    title: "Référence du dossier",
    content: "Le numéro du sinistre et son statut seront affichés ici.",
    placement: "bottom",
  },
  {
    target: ".dsv2-kpis",
    title: "Résumé financier",
    content: "Les KPIs financiers donnent l'état de la provision et des paiements.",
    placement: "top",
  },
  {
    target: ".dsv2-track",
    title: "Consommation de la provision",
    content: "Barre visuelle montrant la part payée, en cours et disponible de la provision.",
    placement: "top",
  },
  {
    target: ".dsv2-tabbar",
    title: "Sections du dossier",
    content: "Utilisez ces onglets pour naviguer entre aperçu, parties, évaluations, règlements, documents, etc.",
    placement: "bottom",
  },
];

const DetailsSinistreTour = ({ run, onClose }) => {
  const [steps] = useState(defaultSteps);
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [rect, setRect] = useState(null);
  const tooltipRef = useRef(null);

  useEffect(() => {
    if (run) { setOpen(true); setIndex(0); return; }
    try {
      const seen = localStorage.getItem("DetailsSinistreTour_shown_v" + TOUR_VERSION);
      if (!seen) { setOpen(true); setIndex(0); }
    } catch (e) { }
  }, [run]);

  useEffect(() => {
    if (!open) return;
    const step = steps[index];
    if (!step) return;
    const el = document.querySelector(step.target);
    if (el) {
      const r = el.getBoundingClientRect();
      setRect(r);
      if (r.top < 0 || r.bottom > (window.innerHeight || document.documentElement.clientHeight)) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    } else {
      setRect(null);
    }
  }, [open, index, steps]);

  const close = () => {
    setOpen(false);
    try { localStorage.setItem("DetailsSinistreTour_shown_v" + TOUR_VERSION, "1"); } catch (e) {}
    if (onClose) onClose();
  };
  const next = () => { if (index < steps.length - 1) setIndex(index + 1); else close(); };
  const prev = () => { if (index > 0) setIndex(index - 1); };

  if (!open) return null;

  const step = steps[index] || {};

  const computeTooltipStyle = () => {
    const offset = 12;
    const maxWidth = 420;
    const style = { position: "absolute", pointerEvents: "auto", zIndex: 11001, maxWidth, background: "#fff", padding: 14, borderRadius: 8, boxShadow: "0 12px 30px rgba(0,0,0,0.18)", transition: "transform 220ms cubic-bezier(.2,.9,.3,1), opacity 220ms" };
    if (!rect) { style.left = 20; style.top = 80; return style; }
    const tooltipW = tooltipRef.current ? Math.min(maxWidth, tooltipRef.current.offsetWidth) : Math.min(maxWidth, rect.width + 200);
    const tooltipH = tooltipRef.current ? tooltipRef.current.offsetHeight : 120;
    if (step.placement === "top") {
      style.left = Math.min(window.innerWidth - tooltipW - 12, Math.max(12, rect.left + window.scrollX + rect.width / 2 - tooltipW / 2));
      style.top = rect.top + window.scrollY - tooltipH - offset;
    } else if (step.placement === "left") {
      style.left = rect.left + window.scrollX - tooltipW - offset;
      style.top = Math.max(12, rect.top + window.scrollY + rect.height / 2 - tooltipH / 2);
    } else if (step.placement === "right") {
      style.left = rect.right + window.scrollX + offset;
      style.top = Math.max(12, rect.top + window.scrollY + rect.height / 2 - tooltipH / 2);
    } else {
      style.left = Math.min(window.innerWidth - tooltipW - 12, Math.max(12, rect.left + window.scrollX + rect.width / 2 - tooltipW / 2));
      style.top = rect.bottom + window.scrollY + offset;
    }
    return style;
  };

  const getContent = () => {
    if (step.target === ".dsv2-ref") {
      const refEl = document.querySelector('.dsv2-ref');
      const statusEl = document.querySelector('.dsv2-idline .dsv2-pill');
      const num = refEl ? (refEl.textContent || '').trim() : "—";
      const st = statusEl ? (statusEl.textContent || '').trim() : "—";
      return `Numéro: ${num}\nStatut: ${st}`;
    }
    return step.content || "";
  };

  const tooltipStyle = computeTooltipStyle();

  const renderArrow = () => {
    const base = { width: 12, height: 12, position: 'absolute', transform: 'rotate(45deg)', background: '#fff', boxShadow: '0 2px 6px rgba(0,0,0,0.12)' };
    if (!rect) return null;
    if (step.placement === 'left') {
      return <div style={{ ...base, right: -6, top: '50%', transform: 'translateY(-50%) rotate(45deg)' }} />;
    }
    if (step.placement === 'right') {
      return <div style={{ ...base, left: -6, top: '50%', transform: 'translateY(-50%) rotate(45deg)' }} />;
    }
    if (step.placement === 'top') {
      return <div style={{ ...base, bottom: -6, left: '50%', transform: 'translateX(-50%) rotate(45deg)' }} />;
    }
    return <div style={{ ...base, top: -6, left: '50%', transform: 'translateX(-50%) rotate(45deg)' }} />;
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 11000, pointerEvents: "none" }}>
      <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.45)", pointerEvents: "auto" }} onClick={close}></div>
      {rect && (
        <div style={{
          position: "absolute",
          left: rect.left - 8 + window.scrollX,
          top: rect.top - 8 + window.scrollY,
          width: rect.width + 16,
          height: rect.height + 16,
          boxShadow: "0 0 0 9999px rgba(0,0,0,0.45)",
          borderRadius: 8,
          pointerEvents: "none",
          outline: "2px solid rgba(255,255,255,0.9)",
          transition: "all 220ms ease",
        }} />
      )}

      <div ref={tooltipRef} style={{ ...tooltipStyle, opacity: 0, transform: 'scale(.98)', animation: 'ds-tour-in 220ms forwards' }}>
        {renderArrow()}
        <div style={{ fontWeight: 700, marginBottom: 6 }}>{step.title}</div>
        <div style={{ whiteSpace: 'pre-line', marginBottom: 10, color: "#333" }}>{getContent()}</div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
          <button
            className="dsv2-btn"
            onClick={prev}
            disabled={index === 0}
            style={{ opacity: index === 0 ? 0.5 : 1, pointerEvents: "auto", padding: "6px 10px" }}
          >
            Précédent
          </button>
          <button
            className="dsv2-btn primary"
            onClick={next}
            style={{ pointerEvents: "auto", padding: "6px 12px" }}
          >
            {index === steps.length - 1 ? "Terminer" : "Suivant"}
          </button>
          <button
            className="dsv2-btn"
            onClick={close}
            style={{ pointerEvents: "auto", marginLeft: 6, padding: "6px 10px" }}
          >
            Fermer
          </button>
        </div>
      </div>

      <style>{`@keyframes ds-tour-in { from { opacity: 0; transform: translateY(6px) scale(.98) } to { opacity: 1; transform: translateY(0) scale(1) } }`}</style>
    </div>
  );
};

export default DetailsSinistreTour;
